import {
  AS400_CDB_COLUMN,
  AS400_EXPORT_WORKSHEET_COLUMNS,
  as400ColumnEditKey,
  as400SortColumnOn,
  compareSortValues,
} from '@erp/rpc';
import type {
  As400ExportGroup,
  As400ExportRow,
  As400GridSort,
  As400GridSortDirection,
} from '@erp/rpc';
import { asCellText } from './as400ExportParts';

/**
 * The review grid's column sort (GEN-2714). One sort for the whole review, not
 * one per worksheet: the chevron in a header on any tab sets it, and every
 * worksheet follows it — the server sorts each tab by its own copy of the
 * column where it has one, else by the sorted tab's records — so tabs stay
 * aligned. The rows are ordered by the server, except the review's new rows,
 * which the grid pins ahead of them and sorts itself (see sortNewRows).
 */

/** What the grid needs to show and change the review's sort. */
export interface GridSorting {
  /** The worksheet on screen. */
  group: As400ExportGroup;
  sort: As400GridSort | null;
  onSortChange: (sort: As400GridSort | null) => void;
}

/** The worksheet's sorted column, by visual index. */
export interface SortedColumn {
  index: number;
  direction: As400GridSortDirection;
}

/**
 * The column this worksheet is ordered by under the review's sort (see
 * as400SortColumnOn), or null when nothing is sorted or it lacks the column.
 * `sortKeys` are the worksheet's columns' sort keys, by visual index.
 */
export function sortedColumn(
  sort: As400GridSort | null,
  group: As400ExportGroup,
  sortKeys: readonly (string | null)[],
): SortedColumn | null {
  if (!sort) return null;
  const column = as400SortColumnOn(sort, group, sortKeys);
  const index = column === null ? -1 : sortKeys.indexOf(column);
  return index === -1 ? null : { index, direction: sort.direction };
}

/**
 * The review sort a column's chevron asks for: on the sorted column asc →
 * desc → off, on any other column asc. Undefined when the column has no sort
 * key (a spacer), which leaves the sort as it is.
 */
export function nextSortFor(
  sort: As400GridSort | null,
  group: As400ExportGroup,
  sortKeys: readonly (string | null)[],
  index: number,
): As400GridSort | null | undefined {
  const column = sortKeys[index];
  if (!column) return undefined;
  const current = sortedColumn(sort, group, sortKeys);
  if (current?.index !== index) return { group, column, direction: 'asc' };
  return current.direction === 'asc'
    ? { group, column, direction: 'desc' }
    : null;
}

/** A workbook header on one line (some carry line breaks). */
function headerText(header: string): string {
  return header.replace(/\s+/g, ' ').trim();
}

/** The sorted column's header, as its worksheet shows it. */
export function sortColumnHeader(sort: As400GridSort): string {
  return worksheetColumnHeader(sort.group, sort.column);
}

/** A worksheet column's header, on one line, by its edit key (or the CDB key). */
export function worksheetColumnHeader(
  group: As400ExportGroup,
  columnKey: string,
): string {
  if (columnKey === AS400_CDB_COLUMN) return 'CDB';
  const column = AS400_EXPORT_WORKSHEET_COLUMNS[group].find(
    (candidate) => as400ColumnEditKey(candidate) === columnKey,
  );
  return headerText(column?.header ?? columnKey);
}

/**
 * A worksheet's new rows (authored, saved to the review) under the review's
 * sort. The server sorts the ingest-backed rows; the new rows are pinned ahead
 * of them, so they are sorted among themselves here, by the same comparison —
 * a new row's cells are its staged values, its CDB the division it was added
 * for. `rows` come in canonical order, which breaks ties (the sort is
 * stable), and stays the order on a worksheet without the sorted column: the
 * server orders that one by another tab's records, which a new row has none
 * of. `sortKeys` are the worksheet's columns' sort keys.
 */
export function sortNewRows(
  rows: readonly As400ExportRow[],
  sort: As400GridSort | null,
  group: As400ExportGroup,
  sortKeys: readonly (string | null)[],
): As400ExportRow[] {
  const column = sort ? as400SortColumnOn(sort, group, sortKeys) : null;
  if (!sort || column === null) return [...rows];
  const cell = (row: As400ExportRow) =>
    asCellText(
      column === AS400_CDB_COLUMN
        ? row.values[column]
        : row.stagedValues?.[column],
    );
  return [...rows].sort((a, b) =>
    compareSortValues(cell(a), cell(b), sort.direction),
  );
}
